import { useState } from 'react';
import {
  Badge,
  Button,
  Checkbox,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
} from '@mantine/core';
import { PTW_BRIFING_NOT_MAX_UZUNLUK } from '@isg/validation';
import type { PtwBriefingList } from '@isg/validation';
import { useIzinBrifingEkle, useIzinBrifingSil, useIzinBrifingleri } from '../api/queries';
import { Bolum, KapiSebebi, SatirEylemleri } from '../sozlesme';
import { useKaydedilmemisIcerik } from '../shell/KaydedilmemisIcerik';
import { ANLAM } from '../theme';
import { datetimeLocalDegeri, zamanGoster } from '../lib/format';
import { QueryStatus } from './parts';

/**
 * İŞ ÖNCESİ BRİFİNG — İZİN DETAYININ BÖLÜMÜ (28.09.2026 — Kapsam §16.7.4 ·
 * §14 K-B1…B4).
 *
 * Brifing iznin KENDİ kaydıdır (tür formunun alanı değil): kim verdi · ne
 * zaman · kimler katıldı. Bir izinde birden çok brifing olabilir.
 *
 * ⚠️ **SAF KAYIT (K-B2): UYARI, ROZET, "BRİFİNG YOK" İŞARETİ ÇİZİLMEZ.**
 * Brifingi olmayan izinde bölüm eksiklik SÖYLEMEZ — brifing bir kapı değildir
 * ve sistem brifingsiz işbaşını engellemez. Buraya bir sayaç ya da durum
 * rozeti eklemek ürün kararını ihlal eder.
 *
 * ⚠️ **EKRAN KURAL BİLMEZ:** giriş satırı `yazmaKapisi`ndan çizilir (yalnız
 * iznin güncel sorumlusu, yalnız onaylanmış ve kapanışa girmemiş izinde);
 * yazamayan kullanıcı sebebini görür. Zaman sınırları (gelecek olamaz · onay
 * anından önce olamaz) sunucuda zorlanır; giriş kutusunun `min`/`max` değeri
 * yalnız kolaylıktır.
 *
 * ⚠️ **KONULAR İÇİN YENİ SÖZLÜK YOK:** brifing konuları iznin tür formlarında
 * SEÇİLMİŞ tehlike · önlem · güvenlik ekipmanı kalemleridir, salt okunur.
 */
export function BrifingBolumu({ permitId }: { permitId: string }): React.JSX.Element {
  const veri = useIzinBrifingleri(permitId);
  return (
    <Bolum baslik="İş öncesi brifing" testId="izin-brifing">
      <Stack gap="sm">
        <QueryStatus queries={[veri]} />
        {veri.isSuccess && (
          <>
            <BrifingKonulari konular={veri.data.konular} />
            <BrifingListesi permitId={permitId} veri={veri.data} />
            {veri.data.yazmaKapisi.yazabilir ? (
              <BrifingGirisi permitId={permitId} veri={veri.data} />
            ) : (
              <KapiSebebi
                durum={veri.data.yazmaKapisi}
                nesne="Brifing kaydını"
                fiil="girer"
                testId="izin-brifing-sebep"
              />
            )}
          </>
        )}
      </Stack>
    </Bolum>
  );
}

function BrifingKonulari({
  konular,
}: {
  konular: PtwBriefingList['konular'];
}): React.JSX.Element | null {
  const dolu = konular.filter(
    (k) => k.tehlikeler.length + k.onlemler.length + k.guvenlikEkipmanlari.length > 0,
  );
  if (dolu.length === 0) return null;
  return (
    <Stack gap="xs" data-testid="izin-brifing-konular">
      <Text size="sm" fw={500}>
        Brifing konuları
      </Text>
      {dolu.map((konu) => (
        <Stack key={konu.izinTuruAd} gap={2}>
          <Text size="sm" fw={500}>
            {konu.izinTuruAd}
          </Text>
          <KonuSatiri etiket="Tehlikeler" kalemler={konu.tehlikeler} />
          <KonuSatiri etiket="Önlemler" kalemler={konu.onlemler} />
          <KonuSatiri etiket="Güvenlik ekipmanı" kalemler={konu.guvenlikEkipmanlari} />
        </Stack>
      ))}
    </Stack>
  );
}

function KonuSatiri({
  etiket,
  kalemler,
}: {
  etiket: string;
  kalemler: readonly string[];
}): React.JSX.Element | null {
  if (kalemler.length === 0) return null;
  return (
    <Text size="sm">
      <Text span c="dimmed" size="sm">
        {etiket}:{' '}
      </Text>
      {kalemler.join(' · ')}
    </Text>
  );
}

function BrifingListesi({
  permitId,
  veri,
}: {
  permitId: string;
  veri: PtwBriefingList;
}): React.JSX.Element | null {
  const sil = useIzinBrifingSil();
  if (veri.brifingler.length === 0) return null;
  const yazabilir = veri.yazmaKapisi.yazabilir;
  return (
    <Stack gap={4}>
      <Table data-testid="izin-brifing-listesi" withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Zaman</Table.Th>
            <Table.Th>Brifingi veren</Table.Th>
            <Table.Th>Katılanlar</Table.Th>
            <Table.Th>Not</Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {veri.brifingler.map((brifing) => (
            <Table.Tr key={brifing.id} data-testid="izin-brifing-satiri">
              <Table.Td>{zamanGoster(brifing.verilmeAt)}</Table.Td>
              <Table.Td>{brifing.verenAd}</Table.Td>
              <Table.Td>
                <Stack gap={4}>
                  {brifing.katilimlar.map((katilim) => (
                    <Group
                      key={katilim.id}
                      gap={6}
                      wrap="nowrap"
                      data-testid="izin-brifing-katilim"
                    >
                      <Text size="sm">{katilim.personAd}</Text>
                      <Badge variant="light">{katilim.gorevAd}</Badge>
                      {/* Son katılım satırı kaldırılamaz (sunucu 422 verir) —
                          o durumda brifing kaydının kendisi kaldırılır; düğme
                          çizilmez (§2.40 — çalışamayacak kontrol görünmez). */}
                      {yazabilir && brifing.katilimlar.length > 1 && (
                        <SatirEylemleri
                          digerleri={[
                            {
                              etiket: 'Katılımı kaldır',
                              tehlikeli: true,
                              testId: 'izin-brifing-katilim-kaldir',
                              onClick: () =>
                                sil.mutate({ permitId, hedef: { attendanceId: katilim.id } }),
                            },
                          ]}
                        />
                      )}
                    </Group>
                  ))}
                </Stack>
              </Table.Td>
              <Table.Td>{brifing.notMetni ?? '—'}</Table.Td>
              <Table.Td>
                {yazabilir && (
                  <SatirEylemleri
                    digerleri={[
                      {
                        etiket: 'Brifing kaydını kaldır',
                        tehlikeli: true,
                        testId: 'izin-brifing-kaldir',
                        onClick: () => sil.mutate({ permitId, hedef: { briefingId: brifing.id } }),
                      },
                    ]}
                    testId="izin-brifing-eylemler"
                  />
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {sil.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="izin-brifing-sil-hata">
          {sil.error.message}
        </Text>
      )}
    </Stack>
  );
}

function BrifingGirisi({
  permitId,
  veri,
}: {
  permitId: string;
  veri: PtwBriefingList;
}): React.JSX.Element {
  const ekle = useIzinBrifingEkle();
  // Boş = "şimdi" (sunucu yazar). Kullanıcı geçmişte yapılan brifingi sonradan
  // giriyorsa zamanı kendisi seçer.
  const [zaman, setZaman] = useState('');
  const [secilen, setSecilen] = useState<string[]>([]);
  const [not, setNot] = useState('');
  const kirli = secilen.length > 0 || not.trim() !== '' || zaman !== '';
  useKaydedilmemisIcerik(kirli ? 1 : 0, 'kaydedilmemiş brifing kaydı');

  const sebep =
    veri.katilimciAdaylari.length === 0
      ? 'İzinde katılımcı yok.'
      : secilen.length === 0
        ? 'Brifinge katılanları işaretleyin.'
        : null;

  return (
    <Stack gap="xs" data-testid="izin-brifing-giris">
      <Text size="sm" fw={500}>
        Brifing kaydı gir
      </Text>
      {/* P10 — az kalemli ÇOKLU seçim: onay kutusu ızgarası (açılır listeye gömülmez). */}
      <Checkbox.Group
        label="Brifinge katılanlar"
        value={secilen}
        onChange={setSecilen}
        data-testid="izin-brifing-katilimcilar"
      >
        <Group gap="md" mt="xs" wrap="wrap">
          {veri.katilimciAdaylari.map((aday) => (
            <Checkbox
              key={aday.participantId}
              value={aday.participantId}
              label={`${aday.personAd} — ${aday.gorevAd}`}
              data-testid="izin-brifing-katilimci"
            />
          ))}
        </Group>
      </Checkbox.Group>
      <Group align="start" grow>
        <TextInput
          label="Brifing zamanı"
          description="Boş bırakılırsa şimdi yazılır."
          type="datetime-local"
          value={zaman}
          min={
            veri.enErkenVerilmeAt ? datetimeLocalDegeri(new Date(veri.enErkenVerilmeAt)) : undefined
          }
          max={datetimeLocalDegeri(new Date())}
          onChange={(olay) => setZaman(olay.currentTarget.value)}
          data-testid="izin-brifing-zaman"
        />
        <Textarea
          label="Not"
          autosize
          minRows={1}
          maxLength={PTW_BRIFING_NOT_MAX_UZUNLUK}
          value={not}
          onChange={(olay) => setNot(olay.currentTarget.value)}
          data-testid="izin-brifing-not"
        />
      </Group>
      {ekle.isError && (
        <Text size="sm" c={ANLAM.engel} role="alert" data-testid="izin-brifing-hata">
          {ekle.error.message}
        </Text>
      )}
      <Group justify="flex-end" align="center">
        {sebep !== null && (
          <Text size="xs" c="dimmed" data-testid="izin-brifing-kaydet-sebep">
            {sebep}
          </Text>
        )}
        <Button
          data-testid="izin-brifing-kaydet"
          disabled={sebep !== null}
          loading={ekle.isPending}
          onClick={() =>
            ekle.mutate(
              {
                permitId,
                girdi: {
                  ...(zaman !== '' ? { verilmeAt: new Date(zaman).toISOString() } : {}),
                  notMetni: not.trim() === '' ? null : not.trim(),
                  participantIds: secilen,
                },
              },
              {
                onSuccess: () => {
                  setSecilen([]);
                  setNot('');
                  setZaman('');
                },
              },
            )
          }
        >
          Brifingi kaydet
        </Button>
      </Group>
    </Stack>
  );
}
